/**
 * Composant d'indicateur de force de mot de passe
 * Affiche les critères de validation avec des coches
 */

import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { PasswordValidationResult } from '../utils/passwordValidator';
import { useTranslation } from 'react-i18next';

interface PasswordStrengthIndicatorProps {
  validationResult: PasswordValidationResult;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ validationResult }) => {
  const { t } = useTranslation();
  
  // Calcul du nombre de critères satisfaits pour la barre de progression
  const criteriaCount = [
    validationResult.hasMinLength,
    validationResult.hasUpperCase,
    validationResult.hasLowerCase,
    validationResult.hasDigit,
    validationResult.hasSpecialChar
  ].filter(Boolean).length;
  
  // Pourcentage de complétion pour la barre de progression
  const completionPercentage = (criteriaCount / 5) * 100;
  
  // Détermination de la couleur de la barre en fonction du pourcentage
  let barColor = '#f44336'; // Rouge par défaut
  if (completionPercentage >= 80) {
    barColor = '#4caf50'; // Vert si >= 80%
  } else if (completionPercentage >= 60) {
    barColor = '#ff9800'; // Orange si >= 60%
  } else if (completionPercentage >= 40) {
    barColor = '#ffeb3b'; // Jaune si >= 40%
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {/* Barre de progression */}
      <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, mb: 1 }}>
        <Box
          sx={{
            height: 8,
            borderRadius: 1,
            width: `${completionPercentage}%`,
            bgcolor: barColor,
            transition: 'width 0.3s, background-color 0.3s'
          }}
        />
      </Box>
      
      {/* Liste des critères */}
      <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1 }}>
        {/* Critère de longueur minimale */}
        <ListItem>
          <ListItemIcon>
            {validationResult.hasMinLength ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={t('auth.passwordCriteria.minLength')} />
        </ListItem>
        
        {/* Critères de complexité */}
        <ListItem>
          <ListItemIcon>
            {validationResult.hasUpperCase ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={t('auth.passwordCriteria.upperCase')} />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            {validationResult.hasLowerCase ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={t('auth.passwordCriteria.lowerCase')} />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            {validationResult.hasDigit ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={t('auth.passwordCriteria.digit')} />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            {validationResult.hasSpecialChar ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={t('auth.passwordCriteria.specialChar')} />
        </ListItem>
      </List>
      
      {/* Message de synthèse */}
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: validationResult.isValid ? 'success.main' : 'error.main' }}>
        {validationResult.isValid 
          ? t('auth.passwordCriteria.valid') 
          : t('auth.passwordCriteria.invalid')}
      </Typography>
    </Box>
  );
};

export default PasswordStrengthIndicator;
